import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, MapPin, UtensilsCrossed } from "lucide-react";
import api from "@/lib/api";
import {
  Reveal,
  Marquee,
  PhotoSlot,
  InfoCards,
  EmptyState,
  useSettings,
  siteText,
  DEFAULT_HERO,
  UniverseSection,
} from "@/components/shared";
import CustomSections from "@/components/layout/CustomSections";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.7 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HIGHLIGHTS = [
  {
    to: "/carte",
    title: "La Carte",
    text: "Entrées, plats, desserts, tapas à partager, cocktails, vins et champagnes.",
    testId: "highlight-carte",
  },
  {
    to: "/notre-histoire",
    title: "Notre Histoire",
    text: "Les origines, la philosophie et le savoir-faire du Palais Maasaï.",
    testId: "highlight-histoire",
  },
  {
    to: "/evenements",
    title: "Événements",
    text: "Les temps forts de la maison, soirées et rendez-vous à venir.",
    testId: "highlight-evenements",
  },
];

function AmbianceSection() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setPhotos(r.data))
      .catch(() => {});
  }, []);
  const shown = photos.slice(0, 6);

  return (
    <section className="border-t border-white/5 px-6 py-28 md:px-12 md:py-40" data-testid="ambiance-section">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="overline mb-6">L'ambiance</p>
          <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
            L'expérience Palais Maasaï
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(shown.length === 0 ? [null, null, null] : shown).map((photo, i) => (
            <Reveal key={photo?.id || i} delay={(i % 3) * 0.1} className={i % 3 === 1 ? "lg:mt-16" : ""}>
              <PhotoSlot
                src={photo?.photo}
                alt={photo?.caption || "Palais Maasaï"}
                ratio="aspect-[3/4]"
                testId={photo ? `ambiance-photo-${photo.id}` : `ambiance-placeholder-${i}`}
              />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <Link
            to="/galerie"
            className="group inline-flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-copper"
            data-testid="ambiance-gallery-link"
          >
            Voir la galerie
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function AvisSection() {
  const settings = useSettings();
  const [avis, setAvis] = useState([]);
  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => setAvis(r.data))
      .catch(() => {});
  }, []);
  const googleRating = settings?.google_rating
    ? parseFloat(String(settings.google_rating).replace(",", "."))
    : null;
  const avg =
    googleRating ??
    (avis.length ? avis.reduce((s, a) => s + a.rating, 0) / avis.length : null);
  const reviewsCount = settings?.google_reviews_count || avis.length;

  return (
    <section className="border-t border-white/5 px-6 py-28 md:px-12 md:py-36" data-testid="avis-section">
      <div className="mx-auto max-w-6xl">
        <Reveal className="text-center">
          <p className="overline mb-6">Ils parlent de nous</p>
          {avg !== null && (
            <div className="mb-8">
              <span className="text-6xl font-bold tracking-tight text-ivory md:text-7xl" data-testid="avis-average">
                {avg.toFixed(1).replace(".", ",")}
              </span>
              <div className="mt-4 flex items-center justify-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${i < Math.round(avg) ? "fill-gold text-gold" : "text-ivory/15"}`}
                    strokeWidth={1}
                  />
                ))}
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-ivory/40">
                Basé sur {reviewsCount} avis Google
              </p>
            </div>
          )}
          <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl">
            Ce que disent nos clients
          </h2>
        </Reveal>

        {avis.length === 0 ? (
          <div className="mt-16">
            <EmptyState text="Les avis de nos clients seront bientôt affichés ici." />
          </div>
        ) : (
          <div className="mt-16 grid gap-6 md:grid-cols-3" data-testid="avis-cards">
            {avis.slice(0, 3).map((a, i) => (
              <Reveal key={a.id} delay={i * 0.1}>
                <figure
                  className="flex h-full flex-col rounded-lg border border-white/10 bg-coal/60 p-8"
                  data-testid={`avis-card-${a.id}`}
                >
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star
                        key={j}
                        className={`h-3.5 w-3.5 ${j < a.rating ? "fill-gold text-gold" : "text-ivory/15"}`}
                        strokeWidth={1}
                      />
                    ))}
                  </div>
                  <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-ivory/65">
                    « {a.text} »
                  </blockquote>
                  <figcaption className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                    {a.author}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        {settings?.google_url && (
          <Reveal className="mt-14 text-center">
            <a
              href={settings.google_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              data-testid="avis-google-link"
            >
              Voir tous les avis Google
            </a>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const settings = useSettings();
  const heroImage = settings?.hero_image || DEFAULT_HERO;
  const design = settings?.design || {};
  const hidden = design.hidden_sections || [];
  const heroScale = design.hero_scale || 1;
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative flex h-screen min-h-[640px] items-center justify-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img
            src={heroImage}
            alt="Palais Maasaï"
            className="hero-zoom h-full w-full object-cover"
            data-testid="hero-image"
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />

        <div className="relative z-10 px-6 text-center">
          <h1
            className="whitespace-nowrap font-bold leading-[0.95] tracking-tight text-copper"
            style={{ fontSize: `clamp(${1.9 * heroScale}rem, ${6.7 * heroScale}vw, ${6.3 * heroScale}rem)` }}
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className="block"
                data-testid="hero-title-line-0"
              >
                PALAIS&nbsp;MAASAÏ
              </motion.span>
            </span>
          </h1>

          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mx-auto mt-8 max-w-2xl text-sm font-medium uppercase tracking-[0.25em] text-ivory/70 md:text-base"
            data-testid="hero-subtitle"
          >
            {siteText(settings, "hero_subtitle") ||
              "Restaurant • Bar Lounge • Cuisine africaine contemporaine à Dijon"}
          </motion.p>

          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link to="/reservation" className="btn-primary" data-el="palais.hero_cta" data-testid="hero-reserve-button">
              Réserver une table
            </Link>
            <Link to="/carte" className="btn-gold" data-testid="hero-menu-button">
              Découvrir la carte
            </Link>
          </motion.div>

          <motion.div
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
            data-testid="hero-highlights"
          >
            {[
              { icon: Star, label: "Excellents avis Google" },
              { icon: MapPin, label: "Dijon Centre" },
              { icon: UtensilsCrossed, label: "Réservation conseillée" },
            ].map((item) => (
              <span
                key={item.label}
                className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-ivory/60"
              >
                <item.icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                {item.label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        >
          <div className="h-14 w-px overflow-hidden bg-white/15">
            <motion.div
              className="h-1/2 w-px bg-gold"
              animate={{ y: [0, 56] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </section>

      {/* Intro */}
      {!hidden.includes("intro") && (
      <section className="px-6 py-28 md:px-12 md:py-40">
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="overline mb-6">Bienvenue</p>
            <h2 className="text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Le Palais Maasaï
            </h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-ivory/60 md:text-lg" data-testid="home-intro-text">
              {siteText(settings, "home_intro") || "Texte à venir."}
            </p>
            <Link
              to="/notre-histoire"
              className="group mt-10 inline-flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-copper"
              data-testid="home-story-link"
            >
              Découvrir notre histoire
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <PhotoSlot ratio="aspect-[4/5]" className="ml-auto max-w-md" testId="home-intro-photo" />
          </Reveal>
        </div>
      </section>
      )}

      {!hidden.includes("marquee") && (
      <Marquee
        items={[
          "Cuisine africaine contemporaine",
          "Dijon",
          "Palais Maasaï",
          "Convivialité",
          "Savoir-faire",
        ]}
      />
      )}

      {/* Highlights */}
      {!hidden.includes("highlights") && (
      <section className="px-6 py-28 md:px-12 md:py-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="overline mb-6">La maison</p>
            <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Découvrir le Palais Maasaï
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
            {HIGHLIGHTS.map((item, i) => (
              <Reveal key={item.to} delay={i * 0.12}>
                <Link to={item.to} className="group block" data-testid={item.testId}>
                  <PhotoSlot ratio="aspect-[3/4]" />
                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-ivory transition-colors group-hover:text-gold">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/50">{item.text}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold">
                    Découvrir
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                      strokeWidth={1.5}
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {!hidden.includes("ambiance") && <AmbianceSection />}

      {!hidden.includes("avis") && <AvisSection />}

      {/* Horaires & informations */}
      {!hidden.includes("horaires") && (
      <section className="border-t border-white/5 px-6 py-28 md:px-12 md:py-36">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="overline mb-6">Informations pratiques</p>
            <h2 className="text-3xl font-bold tracking-tight text-ivory sm:text-4xl">
              Horaires & contact
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-14">
            <InfoCards />
          </Reveal>
        </div>
      </section>
      )}

      <CustomSections page="palais" />

      {/* Cartes cadeaux */}
      {!hidden.includes("cadeaux") && (
      <section className="border-t border-white/5 px-6 py-28 md:px-12 md:py-36" data-testid="home-gift-section">
        <div className="mx-auto max-w-6xl text-center">
          <Reveal>
            <p className="overline mb-6">Cartes cadeaux</p>
            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Offrez une expérience gourmande
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-ivory/55 md:text-base">
              Choisissez le montant, personnalisez votre message et envoyez la carte directement à
              la personne de votre choix — par email ou en PDF à imprimer.
            </p>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link to="/cartes-cadeaux" className="btn-primary" data-testid="home-cta-gift">
                Offrir une carte cadeau
              </Link>
              <Link to="/espace" className="btn-outline" data-testid="home-cta-espace">
                Mon espace
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      <UniverseSection current="palais" />

      {/* CTA */}
      {!hidden.includes("cta") && (
      <section className="border-t border-white/5 px-6 py-28 md:px-12 md:py-36">
        <div className="mx-auto max-w-6xl text-center">
          <Reveal>
            <p className="overline mb-6">Réservations</p>
            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Réservez votre table
            </h2>
            <div className="mt-12">
              <Link to="/reservation" className="btn-primary" data-testid="home-cta-reserve">
                Réserver
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      )}
    </div>
  );
}
